import React from "react";
import { Header, Label, Table, Checkbox, Icon, Popup } from "semantic-ui-react";
import { Trans, useTranslation } from "react-i18next";
import { format } from "date-fns";

import CancelContainer from "./CancelContainer";
import { FORMAT_DATE_TIME_SHORTEST } from "../../../../common/dateFormats";
import RegisterByAdminContainer from "./RegisterByAdminContainer";
import { MASTER_TARIFF, CREDIT } from "../../../tariffs/models";
import { Link } from "react-router-dom";

function Registrations({
  trainingUnit,
  registrations,
  registrationList,
  waitingList,
  canceledList,
  handleRefresh,
  handleConfirmAttendance,
  handleDisconfirmAttendance
}) {
  const { t } = useTranslation();

  const renderRegistration = registration => {
    let tariffTitle = registration.tariffBooking.tariff.title;

    if (tariffTitle === MASTER_TARIFF) {
      tariffTitle = (
        <Trans i18nKey="coursePlan.details.registrations.masterTariffTitle" />
      );
    }

    if (registration.tariffBooking.tariff.tariffModel === CREDIT) {
      tariffTitle = (
        <>
          {tariffTitle}{" "}
          <Label
            circular
            title={t("coursePlan.details.registrations.creditCount")}
          >
            {registration.tariffBooking.creditCount}
          </Label>
        </>
      );
    }

    return (
      <Table.Row key={registration.id}>
        <Table.Cell>
          <Checkbox
            toggle
            checked={registration.attended}
            onChange={() => {
              const fn = registration.attended
                ? handleDisconfirmAttendance
                : handleConfirmAttendance;
              fn({
                trainingUnit,
                registration
              });
            }}
          />
        </Table.Cell>

        <Table.Cell width={5}>
          <Link to={`/members/${registration.member.id}`}>
            {registration.member.firstName} {registration.member.lastName}
          </Link>
          {registration.member.personalNote && (
            <>
              {" "}
              <Popup
                trigger={<Icon name="info circle" color="grey" />}
                content={
                  registration.member.personalNote.length > 500
                    ? registration.member.personalNote
                        .substring(0, 500)
                        .concat(" ...")
                    : registration.member.personalNote
                }
                position="right center"
              />
            </>
          )}
        </Table.Cell>

        <Table.Cell collapsing>
          {registration.tariffBooking.tariff.isTrialTariff && (
            <Label basic color="blue">
              <Trans i18nKey="coursePlan.details.registrations.isTrialTariff" />
            </Label>
          )}
        </Table.Cell>
        <Table.Cell>{registration.member.emailAddress}</Table.Cell>
        <Table.Cell singleLine>{tariffTitle}</Table.Cell>
        <Table.Cell collapsing textAlign="right">
          {format(
            new Date(registration.registrationDate),
            FORMAT_DATE_TIME_SHORTEST
          )}
        </Table.Cell>
        <Table.Cell collapsing textAlign="right">
          <CancelContainer
            trainingUnitId={trainingUnit.id}
            memberId={registration.member.id}
            onFinished={handleRefresh}
          />
        </Table.Cell>
      </Table.Row>
    );
  };

  return (
    <>
      <Header as="h4">
        <Trans i18nKey="coursePlan.details.header.registrations" />
      </Header>
      {registrationList.length > 0 ? (
        <div className="scrolling content">
          <Table unstackable>
            <Table.Body>
              {registrationList.map(registration =>
                renderRegistration(registration)
              )}
            </Table.Body>
          </Table>
        </div>
      ) : (
        <Trans i18nKey="coursePlan.details.registrations.emptyRegistrationList" />
      )}

      <Header as="h4">
        <Trans i18nKey="coursePlan.details.header.waitingList" />
      </Header>
      {waitingList.length > 0 ? (
        <div className="scrolling content">
          <Table unstackable>
            <Table.Body>
              {waitingList.map(registration => (
                <Table.Row key={registration.id}>
                  <Table.Cell width={5}>
                    <Link to={`/members/${registration.member.id}`}>
                      {registration.member.firstName}{" "}
                      {registration.member.lastName}
                    </Link>
                  </Table.Cell>

                  <Table.Cell>{registration.member.emailAddress}</Table.Cell>
                  <Table.Cell collapsing textAlign="right">
                    {format(
                      new Date(registration.registrationDate),
                      FORMAT_DATE_TIME_SHORTEST
                    )}
                  </Table.Cell>
                  <Table.Cell collapsing textAlign="right">
                    <CancelContainer
                      trainingUnitId={trainingUnit.id}
                      memberId={registration.member.id}
                      onFinished={handleRefresh}
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      ) : (
        <Trans i18nKey="coursePlan.details.registrations.emptyWaitingList" />
      )}

      {trainingUnit.active && (
        <>
          <Header as="h4">
            <Trans i18nKey="coursePlan.details.header.manualRegistration" />
          </Header>
          <RegisterByAdminContainer
            trainingUnitId={trainingUnit.id}
            registrations={registrations}
            onFinished={handleRefresh}
          />
        </>
      )}

      <Header as="h4">
        <Trans i18nKey="coursePlan.details.header.canceledList" />
      </Header>
      {canceledList.length > 0 ? (
        <div className="scrolling content">
          <Table unstackable>
            <Table.Body>
              {canceledList.map(registration => (
                <Table.Row key={registration.id}>
                  <Table.Cell width={5}>
                    <Link to={`/members/${registration.member.id}`}>
                      {registration.member.firstName}{" "}
                      {registration.member.lastName}
                    </Link>
                  </Table.Cell>

                  <Table.Cell>{registration.member.emailAddress}</Table.Cell>
                  <Table.Cell collapsing textAlign="right">
                    {format(
                      new Date(registration.registrationDate),
                      FORMAT_DATE_TIME_SHORTEST
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      ) : (
        <Trans i18nKey="coursePlan.details.registrations.emptyCanceledList" />
      )}
    </>
  );
}

Registrations.displayName = "coursePlan/details/registrations/Registrations";

export default React.memo(Registrations);
